import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FeedbackForm from '../../components/feedback/FeedbackForm';
import { useModal } from '../../hooks/useModal';
import InfoModal from '../../components/ui/InfoModal';
import feedbackService from '../../utils/feedbackService';

// Memoized MetricCard component for better performance
const MetricCard = React.memo(({ title, value, change, icon, color = 'primary' }) => (
  <div className="bg-surface rounded-lg border border-border p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
        <Icon name={icon} size={24} className={`text-${color}`} />
      </div>
      {change !== undefined && change !== null && (
        <div className={`flex items-center space-x-1 ${
          change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600'
        }`}>
          <Icon name={change > 0 ? 'TrendingUp' : change < 0 ? 'TrendingDown' : 'Minus'} size={16} />
          <span className="text-sm font-medium">{Math.abs(change)}%</span>
        </div>
      )}
    </div>
    <div>
      <h3 className="text-2xl font-bold text-text-primary mb-1">
        {value !== undefined && value !== null ? value : '-'}
      </h3>
      <p className="text-text-secondary text-sm">{title}</p>
    </div>
  </div>
));

// Memoized ProgressBar component
const ProgressBar = React.memo(({ percentage, color = 'primary' }) => (
  <div className="w-full bg-secondary-100 rounded-full h-2">
    <div
      className={`bg-${color} h-2 rounded-full transition-all duration-300`}
      style={{ width: `${Math.min(percentage || 0, 100)}%` }}
    />
  </div>
));

// Optimized tab components
const OverviewTab = React.memo(({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Jobs Posted"
        value={data.overview?.total_jobs_posted}
        change={12}
        icon="Briefcase"
        color="blue"
      />
      <MetricCard
        title="Total Applications"
        value={data.overview?.total_applications}
        change={8}
        icon="Users"
        color="green"
      />
      <MetricCard
        title="Interviews Scheduled"
        value={data.overview?.interviews_scheduled}
        change={-3}
        icon="Calendar"
        color="purple"
      />
      <MetricCard
        title="Hires Made"
        value={data.overview?.hires_made}
        change={25}
        icon="UserCheck"
        color="primary"
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Avg. Time to Hire"
        value={data.overview?.average_time_to_hire ? `${Math.round(data.overview.average_time_to_hire)} days` : '-'}
        change={-15}
        icon="Clock"
        color="orange"
      />
      <MetricCard
        title="Cost per Hire"
        value={data.overview?.cost_per_hire ? `$${Math.round(data.overview.cost_per_hire)}` : '-'}
        change={-8}
        icon="DollarSign"
        color="red"
      />
      <MetricCard
        title="Conversion Rate"
        value={data.overview?.application_to_hire_rate ? `${data.overview.application_to_hire_rate}%` : '-'}
        change={18}
        icon="Target"
        color="green"
      />
      <MetricCard
        title="Active Jobs"
        value={data.overview?.active_jobs}
        icon="Activity"
        color="blue"
      />
    </div>
  </div>
));

const HiringFunnelTab = React.memo(({ data }) => (
  <div className="space-y-6">
    <div className="bg-surface rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-text-primary mb-6">Hiring Funnel Analysis</h3>
      <div className="space-y-4">
        {data.hiring_funnel && Object.keys(data.hiring_funnel).length > 0 ? (
          Object.entries(data.hiring_funnel).map(([stage, count]) => {
            const total = Object.values(data.hiring_funnel).reduce((sum, val) => sum + val, 0);
            const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
            
            return (
              <div key={stage} className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text-primary capitalize">
                      {stage.replace('_', ' ')}
                    </span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-text-secondary">{count} candidates</span>
                      <span className="text-sm text-text-secondary">{percentage}% conversion</span>
                    </div>
                  </div>
                  <ProgressBar percentage={percentage} />
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-6 text-text-secondary">
            <p>No hiring funnel data available for the selected time period.</p>
            <p className="text-sm mt-2">Try selecting a different date range or adding more job postings.</p>
          </div>
        )}
      </div>
    </div>
  </div>
));

const JobPerformanceTab = React.memo(({ data }) => (
  <div className="space-y-6">
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary">Job Performance Analysis</h3>
      </div>
      {data.job_performance && data.job_performance.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Job Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Applications
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Hires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Time to Hire
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Conversion Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.job_performance.map((job) => (
                <tr key={job.job_id} className="hover:bg-secondary-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-text-primary">{job.title || 'Untitled Job'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary">{job.applications || 0}</td>
                  <td className="px-6 py-4 text-sm text-text-primary">{job.hires || 0}</td>
                  <td className="px-6 py-4 text-sm text-text-primary">
                    {job.time_to_hire ? `${Math.round(job.time_to_hire)} days` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary">{job.conversion_rate || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-10 text-text-secondary">
          <p>No job performance data available for the selected time period.</p>
          <p className="text-sm mt-2">Try selecting a different date range or add more job postings.</p>
        </div>
      )}
    </div>
  </div>
));

const SourceAnalysisTab = React.memo(({ data }) => {
  // Transform real source performance data with enhanced calculations
  const sourceData = useMemo(() => {
    if (!data.source_analysis || !Array.isArray(data.source_analysis) || data.source_analysis.length === 0) {
      return [];
    }

    const colorMap = {
      'direct': 'blue',
      'referral': 'primary',
      'linkedin': 'purple',
      'job_boards': 'green',
      'social_media': 'orange',
      'company_website': 'teal',
      'other': 'gray'
    };

    return data.source_analysis.map(source => {
      const applications = Math.round(source.applications || 0);
      const qualityScore = source.quality_score || 0;
      const hires = Math.round(applications * (qualityScore / 100) * 0.15); // Estimate hires based on quality
      const conversionRate = applications > 0 ? Math.round((hires / applications) * 100) : 0;
      const costPerHire = hires > 0 ? Math.round(150 + (applications * 5) + ((10 - qualityScore) * 20)) : 0;

      return {
        name: source.source.charAt(0).toUpperCase() + source.source.slice(1).replace('_', ' '),
        applications: applications,
        hires: hires,
        conversionRate: conversionRate,
        costPerHire: costPerHire,
        qualityScore: Math.round(qualityScore * 10) / 10, // Round to 1 decimal
        color: colorMap[source.source] || 'gray'
      };
    }).sort((a, b) => b.applications - a.applications); // Sort by applications descending
  }, [data.source_analysis]);

  if (!sourceData.length) {
    return (
      <div className="space-y-6">
        <div className="text-center py-10 text-text-secondary">
          <div className="mb-4">
            <Icon name="Users" size={48} className="mx-auto text-secondary-300" />
          </div>
          <p className="mb-2">No source analysis data available for the selected time period.</p>
          <p className="text-sm">Add source tracking to your job applications to see detailed analytics here.</p>
        </div>
        
        {/* Tips for source tracking */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Lightbulb" size={20} className="text-primary-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-primary-800 mb-2">Tips for Better Source Tracking</h4>
              <ul className="text-xs text-primary-700 space-y-1">
                <li>• Ask candidates how they found your job posting</li>
                <li>• Use UTM parameters in job board links</li>
                <li>• Track referral sources in application forms</li>
                <li>• Monitor social media engagement</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              {sourceData.reduce((sum, source) => sum + source.applications, 0)}
            </div>
            <div className="text-sm text-text-secondary">Total Applications</div>
          </div>
        </div>
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              {sourceData.reduce((sum, source) => sum + source.hires, 0)}
            </div>
            <div className="text-sm text-text-secondary">Total Hires</div>
          </div>
        </div>
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              {sourceData.length > 0 ? 
                Math.round(sourceData.reduce((sum, source) => sum + source.conversionRate, 0) / sourceData.length) 
                : 0}%
            </div>
            <div className="text-sm text-text-secondary">Avg. Conversion Rate</div>
          </div>
        </div>
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              ${sourceData.length > 0 ? 
                Math.round(sourceData.reduce((sum, source) => sum + source.costPerHire, 0) / sourceData.length) 
                : 0}
            </div>
            <div className="text-sm text-text-secondary">Avg. Cost per Hire</div>
          </div>
        </div>
      </div>

      {/* Source Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sourceData.map((source) => (
          <div key={source.name} className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-text-primary">{source.name}</h4>
              <div className={`px-2 py-1 bg-${source.color}-100 text-${source.color}-700 text-xs font-medium rounded-full`}>
                Quality: {source.qualityScore}/10
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-text-secondary">Applications</span>
                <span className="font-medium text-text-primary">{source.applications}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Estimated Hires</span>
                <span className="font-medium text-text-primary">{source.hires}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Conversion Rate</span>
                <span className="font-medium text-text-primary">{source.conversionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Est. Cost per Hire</span>
                <span className="font-medium text-text-primary">${source.costPerHire}</span>
              </div>
              <ProgressBar percentage={source.conversionRate} color={source.color} />
            </div>
          </div>
        ))}
      </div>

      {/* Insights */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Source Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-3">Top Performing Sources</h4>
            <div className="space-y-2">
              {sourceData
                .sort((a, b) => b.conversionRate - a.conversionRate)
                .slice(0, 3)
                .map((source, index) => (
                  <div key={source.name} className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 bg-${source.color} rounded-full`}></div>
                      <span className="text-sm text-text-primary">{source.name}</span>
                    </div>
                    <span className="text-sm font-medium text-text-primary">{source.conversionRate}%</span>
                  </div>
                ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-3">Most Cost-Effective</h4>
            <div className="space-y-2">
              {sourceData
                .filter(source => source.costPerHire > 0)
                .sort((a, b) => a.costPerHire - b.costPerHire)
                .slice(0, 3)
                .map((source, index) => (
                  <div key={source.name} className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 bg-${source.color} rounded-full`}></div>
                      <span className="text-sm text-text-primary">{source.name}</span>
                    </div>
                    <span className="text-sm font-medium text-text-primary">${source.costPerHire}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const TimeTrendsTab = React.memo(({ data }) => {
  const timeTrendsData = useMemo(() => {
    if (!data.time_trends) return null;
    
    const trends = data.time_trends;
    const summary = trends.summary || {};
    
    return {
      daily: trends.daily_trends || [],
      weekly: trends.weekly_trends || [],
      monthly: trends.monthly_trends || [],
      summary: {
        totalPeriods: summary.total_periods || 0,
        avgApplicationsPerPeriod: summary.avg_applications_per_period || 0,
        trendDirection: summary.trend_direction || 'stable',
        growthRate: summary.growth_rate || 0,
        peakApplications: summary.peak_applications || 0,
        dataQuality: summary.data_quality || 'insufficient'
      }
    };
  }, [data.time_trends]);

  const formatTrendData = (trendData, timeframe) => {
    if (!trendData || trendData.length === 0) return [];
    
    return trendData.map(item => ({
      period: new Date(item.period).toLocaleDateString(),
      applications: item.applications || 0,
      shortlisted: item.shortlisted || 0,
      hired: item.hired || 0,
      conversionRate: item.applications > 0 ? ((item.hired / item.applications) * 100).toFixed(1) : '0.0'
    }));
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up': return { icon: 'TrendingUp', color: 'text-green-600', bg: 'bg-green-50' };
      case 'down': return { icon: 'TrendingDown', color: 'text-red-600', bg: 'bg-red-50' };
      default: return { icon: 'Minus', color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  const getDataQualityColor = (quality) => {
    switch (quality) {
      case 'good': return 'text-green-600';
      case 'fair': return 'text-yellow-600';
      default: return 'text-red-600';
    }
  };

  if (!timeTrendsData) {
    return (
      <div className="space-y-6">
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-6">Time Trends</h3>
          <div className="text-center py-10 text-text-secondary">
            <div className="mb-4">
              <Icon name="BarChart3" size={48} className="mx-auto text-secondary-300" />
            </div>
            <p className="mb-2">No time trend data available for the selected time period.</p>
            <p className="text-sm">Try selecting a different date range or wait for more data to accumulate.</p>
          </div>
        </div>
      </div>
    );
  }

  const { summary } = timeTrendsData;
  const trendIndicator = getTrendIcon(summary.trendDirection);
  const weeklyData = formatTrendData(timeTrendsData.weekly, 'weekly');
  const dailyData = formatTrendData(timeTrendsData.daily, 'daily');

  return (
    <div className="space-y-6">
      {/* Trend Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Trend Direction</p>
              <div className="flex items-center gap-2 mt-1">
                <div className={`p-1 rounded ${trendIndicator.bg}`}>
                  <Icon name={trendIndicator.icon} size={16} className={trendIndicator.color} />
                </div>
                <span className={`text-lg font-semibold capitalize ${trendIndicator.color}`}>
                  {summary.trendDirection}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-4">
          <p className="text-sm text-text-secondary">Growth Rate</p>
          <p className={`text-2xl font-bold mt-1 ${summary.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {summary.growthRate > 0 ? '+' : ''}{summary.growthRate}%
          </p>
        </div>

        <div className="bg-surface rounded-lg border border-border p-4">
          <p className="text-sm text-text-secondary">Peak Applications</p>
          <p className="text-2xl font-bold text-text-primary mt-1">{summary.peakApplications}</p>
        </div>

        <div className="bg-surface rounded-lg border border-border p-4">
          <p className="text-sm text-text-secondary">Data Quality</p>
          <p className={`text-lg font-semibold mt-1 capitalize ${getDataQualityColor(summary.dataQuality)}`}>
            {summary.dataQuality}
          </p>
        </div>
      </div>

      {/* Weekly Trends Chart */}
      {weeklyData.length > 0 && (
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-6">Weekly Trends</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Week</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Applications</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Shortlisted</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Hired</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Conversion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {weeklyData.map((week, index) => (
                  <tr key={index} className="hover:bg-surface-hover">
                    <td className="px-4 py-3 text-sm text-text-primary">{week.period}</td>
                    <td className="px-4 py-3 text-sm font-medium text-text-primary">{week.applications}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">{week.shortlisted}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">{week.hired}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">{week.conversionRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {summary.dataQuality === 'insufficient' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">Limited Data Available</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Only {summary.totalPeriods} data point(s) available. For more accurate trend analysis, 
                    we recommend having at least 4 weeks of data.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Daily Trends (if available) */}
      {dailyData.length > 0 && (
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-6">Daily Activity (Last 30 Days)</h3>
          
          <div className="grid grid-cols-7 gap-2">
            {dailyData.slice(-35).map((day, index) => {
              const intensity = Math.min(day.applications / Math.max(...dailyData.map(d => d.applications)), 1);
              const bgOpacity = Math.max(0.1, intensity);
              
              return (
                <div
                  key={index}
                  className="aspect-square rounded border border-border bg-primary-50 p-1 text-center"
                  style={{ backgroundColor: `rgba(59, 130, 246, ${bgOpacity})` }}
                  title={`${day.period}: ${day.applications} applications`}
                >
                  <div className="text-xs text-text-primary font-medium">
                    {day.applications}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex items-center justify-between mt-4 text-xs text-text-secondary">
            <span>Less</span>
            <div className="flex gap-1">
              {[0.1, 0.3, 0.5, 0.7, 1].map((opacity, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded border border-border"
                  style={{ backgroundColor: `rgba(59, 130, 246, ${opacity})` }}
                />
              ))}
            </div>
            <span>More</span>
          </div>
        </div>
      )}

      {/* Insights and Recommendations */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Trend Insights</h3>
        
        <div className="space-y-3">
          {summary.trendDirection === 'up' && (
            <div className="flex items-start bg-green-50 border border-green-200 rounded-lg p-4">
              <Icon name="TrendingUp" size={20} className="text-green-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Positive Growth Trend</p>
                <p className="text-sm text-green-700 mt-1">
                  Your application volume is trending upward with a {summary.growthRate}% growth rate. 
                  Keep up the current recruiting strategies!
                </p>
              </div>
            </div>
          )}
          
          {summary.trendDirection === 'down' && (
            <div className="flex items-start bg-red-50 border border-red-200 rounded-lg p-4">
              <Icon name="TrendingDown" size={20} className="text-red-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Declining Trend Detected</p>
                <p className="text-sm text-red-700 mt-1">
                  Application volume has decreased by {Math.abs(summary.growthRate)}%. Consider reviewing 
                  your job postings, expanding your reach, or adjusting compensation packages.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4">
            <Icon name="Lightbulb" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Average Performance</p>
              <p className="text-sm text-blue-700 mt-1">
                You're averaging {summary.avgApplicationsPerPeriod} applications per period. 
                Your peak performance was {summary.peakApplications} applications in a single period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const BenchmarksTab = React.memo(({ data }) => {
  // Industry benchmarks for Virtual Assistant hiring
  const industryBenchmarks = {
    timeToHire: 35, // days
    costPerHire: 3500, // USD
    conversionRate: 12, // percentage
    qualityScore: 7.5, // out of 10
    interviewToHireRatio: 25, // percentage
    applicationResponseTime: 2 // days
  };

  // Calculate user's actual metrics
  const userMetrics = useMemo(() => {
    const overview = data.overview || {};
    const sourceAnalysis = data.source_analysis || [];
    
    // Get average quality score from source analysis
    const avgQualityScore = sourceAnalysis.length > 0 
      ? sourceAnalysis.reduce((sum, source) => sum + (source.quality_score || 0), 0) / sourceAnalysis.length 
      : 0;

    // Calculate interview to hire ratio
    const interviewToHireRatio = overview.interviews_scheduled > 0 && overview.hires_made > 0
      ? (overview.hires_made / overview.interviews_scheduled) * 100
      : 0;

    return {
      timeToHire: overview.average_time_to_hire || 0,
      costPerHire: overview.cost_per_hire || 0,
      conversionRate: overview.application_to_hire_rate || 0,
      qualityScore: avgQualityScore,
      interviewToHireRatio: interviewToHireRatio,
      totalApplications: overview.total_applications || 0,
      totalHires: overview.hires_made || 0
    };
  }, [data]);

  // Calculate performance indicators
  const getPerformanceIndicator = (userValue, industryBenchmark, lowerIsBetter = false) => {
    if (!userValue || userValue === 0) {
      return { status: 'no-data', color: 'gray', textColor: 'text-gray-600' };
    }

    const percentageDiff = lowerIsBetter 
      ? ((industryBenchmark - userValue) / industryBenchmark) * 100
      : ((userValue - industryBenchmark) / industryBenchmark) * 100;

    if (percentageDiff > 20) {
      return { status: 'excellent', color: 'green', textColor: 'text-green-600' };
    } else if (percentageDiff > 0) {
      return { status: 'good', color: 'blue', textColor: 'text-blue-600' };
    } else if (percentageDiff > -20) {
      return { status: 'fair', color: 'yellow', textColor: 'text-yellow-600' };
    } else {
      return { status: 'needs-improvement', color: 'red', textColor: 'text-red-600' };
    }
  };

  // Performance indicators for each metric
  const timeToHireIndicator = getPerformanceIndicator(userMetrics.timeToHire, industryBenchmarks.timeToHire, true);
  const costPerHireIndicator = getPerformanceIndicator(userMetrics.costPerHire, industryBenchmarks.costPerHire, true);
  const conversionRateIndicator = getPerformanceIndicator(userMetrics.conversionRate, industryBenchmarks.conversionRate, false);
  const qualityScoreIndicator = getPerformanceIndicator(userMetrics.qualityScore, industryBenchmarks.qualityScore, false);

  // Generate recommendations based on performance
  const getRecommendations = () => {
    const recommendations = [];

    if (timeToHireIndicator.status === 'needs-improvement') {
      recommendations.push({
        icon: 'Clock',
        title: 'Optimize Time to Hire',
        description: 'Your hiring process is taking longer than industry average. Consider streamlining your interview process and reducing decision-making delays.',
        priority: 'high'
      });
    }

    if (costPerHireIndicator.status === 'needs-improvement') {
      recommendations.push({
        icon: 'DollarSign',
        title: 'Reduce Cost per Hire',
        description: 'Focus on improving your most cost-effective recruitment channels and reducing time spent on low-quality applicants.',
        priority: 'medium'
      });
    }

    if (conversionRateIndicator.status === 'needs-improvement') {
      recommendations.push({
        icon: 'Target',
        title: 'Improve Conversion Rate',
        description: 'Enhance your job descriptions, compensation packages, and candidate experience to attract higher-quality applicants.',
        priority: 'high'
      });
    }

    if (qualityScoreIndicator.status === 'needs-improvement') {
      recommendations.push({
        icon: 'Star',
        title: 'Focus on Quality Sources',
        description: 'Invest more in recruitment channels that bring higher-quality candidates, even if they cost more initially.',
        priority: 'medium'
      });
    }

    // Add positive reinforcement for good performance
    if (recommendations.length === 0) {
      recommendations.push({
        icon: 'Trophy',
        title: 'Excellent Performance',
        description: 'Your hiring metrics are performing well above industry averages. Consider sharing your best practices with other teams.',
        priority: 'success'
      });
    }

    return recommendations;
  };

  const recommendations = getRecommendations();

  // Calculate overall performance score
  const calculateOverallScore = () => {
    const scores = [timeToHireIndicator, costPerHireIndicator, conversionRateIndicator, qualityScoreIndicator];
    const scoreValues = scores.map(indicator => {
      switch (indicator.status) {
        case 'excellent': return 100;
        case 'good': return 80;
        case 'fair': return 60;
        case 'needs-improvement': return 40;
        default: return 0;
      }
    });
    return Math.round(scoreValues.reduce((sum, score) => sum + score, 0) / scoreValues.length);
  };

  const overallScore = calculateOverallScore();

  if (!data.overview || userMetrics.totalApplications === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-6">Industry Benchmarks</h3>
          <div className="text-center py-10 text-text-secondary">
            <div className="mb-4">
              <Icon name="Target" size={48} className="mx-auto text-secondary-300" />
            </div>
            <p className="mb-2">No benchmark data available yet.</p>
            <p className="text-sm">Start hiring to see how you compare against industry standards.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Performance Score */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Overall Performance Score</h3>
          <div className="flex items-center space-x-2">
            <div className={`text-3xl font-bold ${overallScore >= 80 ? 'text-green-600' : overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
              {overallScore}
            </div>
            <span className="text-text-secondary">/100</span>
          </div>
        </div>
        <div className="w-full bg-secondary-100 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${
              overallScore >= 80 ? 'bg-green-500' : overallScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${overallScore}%` }}
          />
        </div>
        <p className="text-sm text-text-secondary mt-2">
          Based on your performance across key hiring metrics compared to industry benchmarks
        </p>
      </div>

      {/* Key Benchmarks */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Key Performance Metrics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="text-center">
            <div className={`text-3xl font-bold ${timeToHireIndicator.textColor} mb-2`}>
              {userMetrics.timeToHire ? Math.round(userMetrics.timeToHire) : 0}
            </div>
            <div className="text-sm text-text-secondary mb-1">days</div>
            <div className="text-sm text-text-primary font-medium">Time to Hire</div>
            <div className="text-xs text-text-secondary mt-1">Industry avg: {industryBenchmarks.timeToHire} days</div>
            <div className={`text-xs font-medium ${timeToHireIndicator.textColor}`}>
              {userMetrics.timeToHire > 0 ? (
                userMetrics.timeToHire < industryBenchmarks.timeToHire 
                  ? `${Math.round(industryBenchmarks.timeToHire - userMetrics.timeToHire)} days faster`
                  : `${Math.round(userMetrics.timeToHire - industryBenchmarks.timeToHire)} days slower`
              ) : 'No data'}
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-3xl font-bold ${costPerHireIndicator.textColor} mb-2`}>
              ${userMetrics.costPerHire ? Math.round(userMetrics.costPerHire).toLocaleString() : 0}
            </div>
            <div className="text-sm text-text-primary font-medium">Cost per Hire</div>
            <div className="text-xs text-text-secondary mt-1">Industry avg: ${industryBenchmarks.costPerHire.toLocaleString()}</div>
            <div className={`text-xs font-medium ${costPerHireIndicator.textColor}`}>
              {userMetrics.costPerHire > 0 ? (
                userMetrics.costPerHire < industryBenchmarks.costPerHire 
                  ? `$${Math.round(industryBenchmarks.costPerHire - userMetrics.costPerHire).toLocaleString()} lower`
                  : `$${Math.round(userMetrics.costPerHire - industryBenchmarks.costPerHire).toLocaleString()} higher`
              ) : 'No data'}
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-3xl font-bold ${conversionRateIndicator.textColor} mb-2`}>
              {userMetrics.conversionRate ? Math.round(userMetrics.conversionRate) : 0}%
            </div>
            <div className="text-sm text-text-primary font-medium">Conversion Rate</div>
            <div className="text-xs text-text-secondary mt-1">Industry avg: {industryBenchmarks.conversionRate}%</div>
            <div className={`text-xs font-medium ${conversionRateIndicator.textColor}`}>
              {userMetrics.conversionRate > 0 ? (
                userMetrics.conversionRate > industryBenchmarks.conversionRate 
                  ? `${Math.round(userMetrics.conversionRate - industryBenchmarks.conversionRate)}% above avg`
                  : `${Math.round(industryBenchmarks.conversionRate - userMetrics.conversionRate)}% below avg`
              ) : 'No data'}
            </div>
          </div>

          <div className="text-center">
            <div className={`text-3xl font-bold ${qualityScoreIndicator.textColor} mb-2`}>
              {userMetrics.qualityScore ? userMetrics.qualityScore.toFixed(1) : '0.0'}
            </div>
            <div className="text-sm text-text-secondary mb-1">out of 10</div>
            <div className="text-sm text-text-primary font-medium">Quality Score</div>
            <div className="text-xs text-text-secondary mt-1">Industry avg: {industryBenchmarks.qualityScore}/10</div>
            <div className={`text-xs font-medium ${qualityScoreIndicator.textColor}`}>
              {userMetrics.qualityScore > 0 ? (
                userMetrics.qualityScore > industryBenchmarks.qualityScore 
                  ? `${(userMetrics.qualityScore - industryBenchmarks.qualityScore).toFixed(1)} above avg`
                  : `${(industryBenchmarks.qualityScore - userMetrics.qualityScore).toFixed(1)} below avg`
              ) : 'No data'}
            </div>
          </div>
        </div>

        {/* Performance Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-text-primary">Performance Breakdown</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                <span className="text-sm text-text-primary">Time to Hire</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 bg-${timeToHireIndicator.color}-500 rounded-full`}></div>
                  <span className={`text-sm font-medium capitalize ${timeToHireIndicator.textColor}`}>
                    {timeToHireIndicator.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                <span className="text-sm text-text-primary">Cost Efficiency</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 bg-${costPerHireIndicator.color}-500 rounded-full`}></div>
                  <span className={`text-sm font-medium capitalize ${costPerHireIndicator.textColor}`}>
                    {costPerHireIndicator.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-text-primary">Quality Metrics</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                <span className="text-sm text-text-primary">Conversion Rate</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 bg-${conversionRateIndicator.color}-500 rounded-full`}></div>
                  <span className={`text-sm font-medium capitalize ${conversionRateIndicator.textColor}`}>
                    {conversionRateIndicator.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-secondary-50 rounded">
                <span className="text-sm text-text-primary">Candidate Quality</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 bg-${qualityScoreIndicator.color}-500 rounded-full`}></div>
                  <span className={`text-sm font-medium capitalize ${qualityScoreIndicator.textColor}`}>
                    {qualityScoreIndicator.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Personalized Recommendations</h3>
        <div className="space-y-4">
          {recommendations.map((rec, index) => (
            <div key={index} className={`flex items-start space-x-3 p-4 rounded-lg border ${
              rec.priority === 'high' ? 'bg-red-50 border-red-200' :
              rec.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
              rec.priority === 'success' ? 'bg-green-50 border-green-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <Icon name={rec.icon} size={20} className={`mt-0.5 ${
                rec.priority === 'high' ? 'text-red-600' :
                rec.priority === 'medium' ? 'text-yellow-600' :
                rec.priority === 'success' ? 'text-green-600' :
                'text-blue-600'
              }`} />
              <div>
                <h4 className={`text-sm font-medium ${
                  rec.priority === 'high' ? 'text-red-800' :
                  rec.priority === 'medium' ? 'text-yellow-800' :
                  rec.priority === 'success' ? 'text-green-800' :
                  'text-blue-800'
                }`}>
                  {rec.title}
                </h4>
                <p className={`text-sm mt-1 ${
                  rec.priority === 'high' ? 'text-red-700' :
                  rec.priority === 'medium' ? 'text-yellow-700' :
                  rec.priority === 'success' ? 'text-green-700' :
                  'text-blue-700'
                }`}>
                  {rec.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Industry Context */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Industry Context</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-3">Virtual Assistant Market Insights</h4>
            <div className="space-y-2 text-sm text-text-secondary">
              <p>• Average VA hiring takes 30-45 days due to skill verification needs</p>
              <p>• Quality VAs typically cost $2,500-$4,500 to recruit and onboard</p>
              <p>• Top performers achieve 15-25% application-to-hire rates</p>
              <p>• Remote work requirements increase candidate pool but screening time</p>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-3">Best Practices</h4>
            <div className="space-y-2 text-sm text-text-secondary">
              <p>• Use skill-based assessments early in the process</p>
              <p>• Clearly define role requirements and expectations</p>
              <p>• Implement structured interview processes</p>
              <p>• Track and optimize your best-performing recruitment sources</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const AdvancedAnalyticsPage = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { infoModal, showInfo, closeInfo } = useModal();
  
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');

  // Memoized configuration
  const dateRanges = useMemo(() => [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '1y', label: 'Last year' }
  ], []);

  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'funnel', label: 'Hiring Funnel', icon: 'Filter' },
    { id: 'performance', label: 'Job Performance', icon: 'Briefcase' },
    { id: 'sources', label: 'Source Analysis', icon: 'Users' },
    { id: 'trends', label: 'Time Trends', icon: 'TrendingUp' },
    { id: 'benchmarks', label: 'Benchmarks', icon: 'Target' }
  ], []);

  // Handle feedback submission
  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    try {
      const result = await feedbackService.submitFeedback({
        userId: userProfile.id,
        type: feedbackType,
        rating: feedbackData.rating,
        comment: feedbackData.comment,
        contextData: {
          page: 'advanced-analytics',
          dateRange: dateRange,
          timestamp: new Date().toISOString()
        }
      });
      
      if (result.success) {
        setShowFeedbackModal(false);
        
        showInfo({
          title: 'Thank You',
          message: 'Thank you for your feedback! We appreciate your input and will review it soon.',
          type: 'success'
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showInfo({
        title: 'Error',
        message: 'There was an error submitting your feedback. Please try again.',
        type: 'error'
      });
    }
  }, [userProfile?.id, feedbackType, dateRange, showInfo]);

  // Optimized data fetching with useCallback
  const fetchAnalyticsData = useCallback(async (refresh = false) => {
    if (!userProfile?.id) return;
    
    if (refresh) setRefreshing(true);
    
    try {
      // First, refresh the analytics data in the database
      await supabase.rpc('refresh_analytics_for_employer', {
        p_employer_id: userProfile.id
      });

      // Then fetch the updated analytics data
      const { data, error } = await supabase
        .from('employer_analytics')
        .select('*')
        .eq('employer_id', userProfile.id)
        .eq('date_range', dateRange)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // Format the data for UI consumption
      const formattedData = formatAnalyticsData(data);
      setAnalyticsData(formattedData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsData(getEmptyAnalyticsData());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userProfile?.id, dateRange]);

  // Memoized data formatting
  const formatAnalyticsData = useCallback((data) => {
    if (!data) {
      return getEmptyAnalyticsData();
    }

    return {
      overview: {
        total_jobs_posted: data.total_jobs_posted || 0,
        total_applications: data.total_applications || 0,
        interviews_scheduled: data.interviews_scheduled || 0,
        hires_made: data.hires_made || 0,
        average_time_to_hire: data.average_time_to_hire ? parseFloat(data.average_time_to_hire) : null,
        cost_per_hire: data.cost_per_hire ? parseFloat(data.cost_per_hire) : null,
        application_to_hire_rate: data.application_to_hire_rate ? parseFloat(data.application_to_hire_rate) : null,
        active_jobs: data.active_jobs || 0
      },
      hiring_funnel: data.hiring_funnel || {},
      job_performance: data.job_performance || [],
      source_analysis: data.source_performance || [],
      time_trends: data.time_trends || {},
      benchmarks: data.benchmarks || {}
    };
  }, []);

  const getEmptyAnalyticsData = useCallback(() => ({
    overview: {
      total_jobs_posted: 0,
      total_applications: 0,
      interviews_scheduled: 0,
      hires_made: 0,
      average_time_to_hire: null,
      cost_per_hire: null,
      application_to_hire_rate: null,
      active_jobs: 0
    },
    hiring_funnel: {},
    job_performance: [],
    source_analysis: [],
    time_trends: [],
    benchmarks: {}
  }), []);

  // Export functionality with performance optimization
  const exportAnalytics = useCallback(async () => {
    try {
      const csvData = [
        ['Metric', 'Value'],
        ['Total Jobs Posted', analyticsData.overview?.total_jobs_posted || 0],
        ['Total Applications', analyticsData.overview?.total_applications || 0],
        ['Interviews Scheduled', analyticsData.overview?.interviews_scheduled || 0],
        ['Hires Made', analyticsData.overview?.hires_made || 0],
        ['Average Time to Hire (days)', analyticsData.overview?.average_time_to_hire || 'N/A'],
        ['Cost per Hire ($)', analyticsData.overview?.cost_per_hire || 'N/A'],
        ['Conversion Rate (%)', analyticsData.overview?.application_to_hire_rate || 'N/A'],
        ['Active Jobs', analyticsData.overview?.active_jobs || 0]
      ];

      const csvContent = csvData.map(row => 
        row.map(field => 
          typeof field === 'string' && field.includes(',') ? `"${field}"` : field
        ).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `analytics_${dateRange}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting analytics:', error);
    }
  }, [analyticsData, dateRange]);

  // Refresh data function
  const refreshData = useCallback(() => {
    fetchAnalyticsData(true);
  }, [fetchAnalyticsData]);

  // Effect for data fetching
  useEffect(() => {
    if (userProfile?.role === 'employer' && userProfile?.id) {
      fetchAnalyticsData();
    } else if (userProfile?.role !== 'employer') {
      navigate('/employer-dashboard');
    }
  }, [userProfile, fetchAnalyticsData, navigate]);

  // Render tab content with performance optimization
  const renderTabContent = useMemo(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={analyticsData} />;
      case 'funnel':
        return <HiringFunnelTab data={analyticsData} />;
      case 'performance':
        return <JobPerformanceTab data={analyticsData} />;
      case 'sources':
        return <SourceAnalysisTab data={analyticsData} />;
      case 'trends':
        return <TimeTrendsTab data={analyticsData} />;
      case 'benchmarks':
        return <BenchmarksTab data={analyticsData} />;
      default:
        return <OverviewTab data={analyticsData} />;
    }
  }, [activeTab, analyticsData]);

  return (
    <div className="min-h-screen bg-background">
      <EmployerHeaderNav />
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Advanced Analytics</h1>
                <p className="text-text-secondary">Detailed insights into your hiring performance and ROI</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  disabled={loading || refreshing}
                  className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  {dateRanges.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageCircle"
                  onClick={() => {
                    setFeedbackType('general');
                    setShowFeedbackModal(true);
                  }}
                  disabled={loading || refreshing}
                >
                  Give Feedback
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={exportAnalytics}
                  disabled={loading || refreshing}
                >
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={refreshing ? "Loader" : "RefreshCw"}
                  onClick={refreshData}
                  disabled={loading || refreshing}
                  className={refreshing ? "animate-spin" : ""}
                >
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Loader" size={40} className="text-primary animate-spin mb-4" />
              <p className="text-text-secondary">Loading analytics data...</p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="bg-surface rounded-lg border border-border mb-6">
                <div className="flex overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-6 py-4 border-b-2 transition-colors duration-200 whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-primary text-primary bg-primary-50'
                          : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-secondary-50'
                      }`}
                    >
                      <Icon name={tab.icon} size={18} />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {renderTabContent}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackForm
          type={feedbackType}
          contextId={null}
          contextTitle="Advanced Analytics Page"
          onSubmit={handleFeedbackSubmit}
          onCancel={() => setShowFeedbackModal(false)}
        />
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          isOpen={true}
          onClose={closeInfo}
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
        />
      )}
    </div>
  );
};

export default AdvancedAnalyticsPage;
